// 🖼️ Logo Service - Fetches real logos for websites
export class LogoService {
  constructor() {
    this.cache = new Map();
    this.fallbackEmojis = {
      'github.com': '🐙',
      'google.com': '🔍',
      'youtube.com': '📺',
      'facebook.com': '📘',
      'twitter.com': '🐦',
      'linkedin.com': '💼',
      'instagram.com': '📷',
      'reddit.com': '🤖',
      'stackoverflow.com': '📚',
      'medium.com': '📝',
      'dev.to': '👩‍💻',
      'codepen.io': '🖊️',
      'figma.com': '🎨',
      'canva.com': '🎨',
      'notion.so': '📋',
      'slack.com': '💬',
      'discord.com': '🎮',
      'zoom.us': '📹',
      'netflix.com': '🎬',
      'spotify.com': '🎵',
      'amazon.com': '📦',
      'apple.com': '🍎',
      'microsoft.com': '🏢',
      'news': '📰',
      'finance': '💰',
      'tech': '💻',
      'design': '🎨',
      'default': '🔗'
    };
  }

  /**
   * Get the best available logo for a URL
   * @param {string} url - The website URL
   * @param {string} title - Optional title for fallback
   * @returns {Promise<{type: 'favicon'|'emoji', src: string}>}
   */
  async getLogo(url, title = '') {
    if (!url) return { type: 'emoji', src: '🔗' };

    // Check cache first
    const cacheKey = this.getDomain(url);
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    try {
      const domain = this.getDomain(url);
      
      // Try multiple favicon sources in order of preference
      const faviconSources = [
        `https://icons.duckduckgo.com/ip3/${domain}.ico`, // High quality
        `https://www.google.com/s2/favicons?domain=${domain}&sz=64`, // Google's cache
        `https://favicon.yandex.net/favicon/${domain}`, // Yandex backup
        `https://${domain}/favicon.ico` // Direct from site
      ];

      // Test favicon sources
      for (const faviconUrl of faviconSources) {
        if (await this.isImageValid(faviconUrl)) {
          const result = { type: 'favicon', src: faviconUrl };
          this.cache.set(cacheKey, result);
          return result;
        }
      }

      // Fallback to emoji
      const emoji = this.getFallbackEmoji(domain, title);
      const result = { type: 'emoji', src: emoji };
      this.cache.set(cacheKey, result);
      return result;

    } catch (error) {
      console.warn('🖼️ Logo service error:', error);
      const emoji = this.getFallbackEmoji(this.getDomain(url), title);
      return { type: 'emoji', src: emoji };
    }
  }

  /**
   * Extract domain from URL
   * @param {string} url 
   * @returns {string}
   */
  getDomain(url) {
    try {
      return new URL(url).hostname.replace('www.', '');
    } catch {
      return url;
    }
  }

  /**
   * Get smart fallback emoji based on domain or content
   * @param {string} domain 
   * @param {string} title 
   * @returns {string}
   */
  getFallbackEmoji(domain, title = '') {
    // Check specific domains first
    for (const [key, emoji] of Object.entries(this.fallbackEmojis)) {
      if (domain.includes(key)) {
        return emoji;
      }
    }

    // Check title/content for keywords
    const content = (domain + ' ' + title).toLowerCase();
    if (content.includes('news') || content.includes('journal') || content.includes('presse')) return '📰';
    if (content.includes('github') || content.includes('code') || content.includes('dev')) return '👩‍💻';
    if (content.includes('design') || content.includes('figma') || content.includes('canva')) return '🎨';
    if (content.includes('ai') || content.includes('chat') || content.includes('bot')) return '🤖';
    if (content.includes('video') || content.includes('youtube') || content.includes('watch')) return '📺';
    if (content.includes('music') || content.includes('spotify') || content.includes('sound')) return '🎵';
    if (content.includes('shop') || content.includes('store') || content.includes('buy')) return '🛒';
    if (content.includes('learn') || content.includes('course') || content.includes('education')) return '📚';
    if (content.includes('doc') || content.includes('guide') || content.includes('manual')) return '📄';
    if (content.includes('finance') || content.includes('bank') || content.includes('money')) return '💰';

    return this.fallbackEmojis.default;
  }

  /**
   * Test if an image URL is valid and loads
   * @param {string} url 
   * @returns {Promise<boolean>}
   */
  async isImageValid(url) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
      
      // Timeout after 2 seconds
      setTimeout(() => resolve(false), 2000);
    });
  }

  /**
   * Preload logos for multiple URLs
   * @param {Array<string>} urls 
   */
  async preloadLogos(urls) {
    const promises = urls.map(url => this.getLogo(url));
    await Promise.allSettled(promises);
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
  }
}

// Export singleton instance
export const logoService = new LogoService(); 